<!--TODO: Удалить после того как везде заменим-->
<script lang="ts">
export type ModelValueType = string | number | any[];

export interface Props {
  list?: any[],
  placeholder?: string,
  width?: string | number,
  isGrouped?: boolean,
  collapseTagTooltip?: boolean,
  optionNameParam?: string,
  optionLabelParam?: string,
  optionValueParam?: string,
  required?: boolean,
  label?: string,
  noScroll?: boolean,
  filterable?: boolean,
  labelSize?: number,
  disabled?: boolean,
  isError?: boolean,
  error?: any,
  remote?: boolean,
  clearable?: boolean,
  teleported?: boolean,
  remoteMethod?: () => void,
  size?: string,
  maxCollapseTags?: number,
  searchParam?: string,
}

export interface Emits {
  'change': [value: ModelValueType], 
  'visible-change': [value: boolean]
}
</script>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { get, isArray } from '@/setup/lodash';
import type { ElSelect } from 'element-plus';

const props = withDefaults(defineProps<Props>(),{
  list: () => [],
  placeholder: '',
  width: '',
  isGrouped: false,
  collapseTagTooltip: false,
  optionNameParam: 'name',
  optionLabelParam: 'name',
  optionValueParam: 'id',
  required: false,
  label: '',
  noScroll: false,
  filterable: false,
  labelSize: 14,
  disabled: false,
  isError: false,
  error: '',
  remote: false,
  clearable: false,
  teleported: true,
  size: 'medium',
  maxCollapseTags: 1,
  searchParam: '',
});

const emit = defineEmits<Emits>();

const modelValue = defineModel<ModelValueType>();
const currentError = ref<string | number | null>(null);
const searchQuery = ref('');
const uiSelectTagsRef = useTemplateRef('uiSelectTags');

const pWidth = computed(() => {
  return props.width === '' ? '100%' : `${props.width}px`;
});

const pPopperClass = computed(() => {
  const classes = ['ui-select-tags__popper'];

  if (props.noScroll) {
    classes.push('ui-select-tags__popper_no-scroll');
  }

  return classes.join(' ');
});

watch(() => props.error, async (newValue, oldValue) => {
  if (newValue !== oldValue) {
    currentError.value = isArray(newValue) ? newValue[0].message : newValue;
  }
}, { immediate: true });

const handleChange = async (value: ModelValueType) => {
  emit('change', value);

  if (!props.filterable || props.remote || !props.searchParam) {
    return;
  }

  searchQuery.value = '';
  if (uiSelectTagsRef.value?.states) {
    uiSelectTagsRef.value.states.inputValue = '';
  }
};
const handleVisibleChange = (value: boolean) => {
  emit('visible-change', value);

  if (!value) {
    searchQuery.value = '';
  }
};

const normalizedSearchQuery = computed(() => searchQuery.value.trim().toLowerCase());

const filteredOptions = computed(() => {
  if (!props.filterable || props.remote || !props.searchParam || normalizedSearchQuery.value === '') {
    return props.list;
  }

  const matcher = (item: any) => get(item, props.searchParam, '').toLowerCase().includes(normalizedSearchQuery.value);

  if (props.isGrouped) {
    return props.list.reduce((acc, group) => {
      const options = get(group, 'options', []).filter(matcher);

      if (options.length) {
        acc.push({
          ...group,
          options,
        });
      }

      return acc;
    }, []);
  }

  return props.list.filter(matcher);
});

const selectFilterMethod = computed(() => {
  if (!props.filterable || props.remote || !props.searchParam) {
    return undefined;
  }

  return (query: string) => {
    searchQuery.value = query;
  };
});


defineExpose({
  uiSelectTagsRef,
});
</script>

<template>
  <div
    :class="[
      'ui-select-tags',
      `ui-select-tags_${size}`,
      { 'ui-select-tags_disabled': disabled },
      { 'ui-select-tags_error': currentError || isError },
    ]"
  >
    <UiLabel
      v-if="label"
      :font-size="labelSize"
      :required="required"
      :label="label"
      :disabled="disabled"
      class="t:mb-8"
    />
    <ElSelect
      ref="uiSelectTags"
      v-model="modelValue"
      :placeholder="placeholder"
      :filterable="filterable"
      :remote="remote"
      :remote-method="remoteMethod"
      :filter-method="selectFilterMethod"
      :max-collapse-tags="maxCollapseTags"
      :clearable="clearable"
      multiple
      collapse-tags
      remote-show-suffix
      :teleported="teleported"
      :collapse-tags-tooltip="collapseTagTooltip"
      :popper-class="pPopperClass"
      :style="{ width: pWidth }"
      @change="handleChange"
      @visible-change="handleVisibleChange"
    >
      <slot name="options">
        <template v-if="isGrouped">
          <ElOptionGroup
            v-for="group in filteredOptions"
            :key="group.label"
            :label="group.label"
          >
            <ElOption
              v-for="item in group.options"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </ElOptionGroup>
        </template>
        <template v-else>
          <ElOption
            v-for="item in filteredOptions"
            :key="item[optionValueParam]"
            :label="item[optionLabelParam]"
            :value="item[optionValueParam]"
          >
            {{ item[optionNameParam] }}
          </ElOption>
        </template>
      </slot>
    </ElSelect>
    <UiHelp
      v-if="currentError"
      is-error
      :text="currentError"
    />
  </div>
</template>

<style lang="scss">
.ui-select-tags{
  .el-select{
    cursor: pointer;

    .el-input .el-select__caret{
      display: flex;
      align-items: center;
      &:before {
        width: 100%;
      }
    }

    .el-select__wrapper{
      box-shadow: none;
      border: 1px solid #d3d3d3;
      background-image: linear-gradient(180deg, #ffffff 0%, #f6f6f6 100%);
      min-height: 30px;
      &.is-focused {
        border-color: var(--input-border-main)
      }
    }
    .el-select__input{
      height: 20px;
    }
    .el-tag{
      border: none;
      border-radius: 4px;
      background-color: #eaeaea;
      color: #303634;
      font-size: 12px;
      font-weight: 500;
      padding: 0 8px;
      width: auto;
      height: 20px;
      display: flex;
      align-items: center;

      &__close{
        border-radius: 3px;
        display: flex;
        justify-content: center;
        align-items: center;
        outline: none;
        line-height: 24px;
        //top: 1px;
        //font-weight: 700;
        //font-size: 16px;

        color: var(--text-negative);
        background-color: transparent;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        path {
          stroke-width: 90px;
          stroke: var(--text-negative);
        }
        &:hover{
          color: var(--text-negative);
          background-color: transparent;
        }
      }
    }
  }


  &__popper {
    z-index: 90000 !important;
    max-width: 600px;
    //.el-popper__arrow {
    //  left: 70px !important;
    //}
    .el-select-dropdown.is-multiple .el-select-dropdown__item {
      span{
        padding-right: 16px;
      }
    }
    .el-select-dropdown.is-multiple .el-select-dropdown__item.is-selected{
      color: var(--text-primary);
      &:after{
        background-color: var(--text-primary);
      }
    }
    .el-select-group__title{
      padding-left: 10px;
      font-size: 12px;
      color: #303634;
      text-transform: uppercase;
      font-weight: 700;
    }

    &_no-scroll{
      .el-select-dropdown {
        .el-select-dropdown__wrap{
          max-height: 100%;
        }
        .el-select-group__wrap{
          padding-bottom: 10px;
          &:after{
            content: none;
          }
        }
      }
    }
  }

  &_error {
    .el-select .el-select__wrapper {
      border-color: var(--text-negative);
    }
  }
  &_disabled {
    .el-select{
      cursor: not-allowed;
    }
    .el-select__wrapper {
      pointer-events: none;
      opacity: 0.5;
    }

  }

  &_medium {
    .el-select__placeholder{
      font-size: 12px;
    }
  }
  &_big {
    .el-select .el-select__wrapper{
      min-height: 34px;
      padding:0 12px;
    }
    .el-select__placeholder{
      font-size: 13px;
    }
  }
}
</style>
